/**
 * Google Ads conversion tracking
 */

import type { AnalyticsUser, ConversionConfig } from './types';

// Ensure gtag is available on window and returns a callable function
function ensureGtag(): (...args: unknown[]) => void {
  const w = window as typeof window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function gtag(...args: unknown[]) {
      w.dataLayer!.push(args);
    };
  }
  return w.gtag;
}

/**
 * Initialize Google Ads tracking
 * Note: The gtag script should be loaded in index.html
 */
export function init(conversionId: string): void {
  const gtag = ensureGtag();
  // Config for Google Ads
  gtag('config', conversionId);
}

/**
 * Hash email using SHA-256 for enhanced conversions
 */
export async function hashEmail(email: string): Promise<string> {
  const normalizedEmail = email.toLowerCase().trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalizedEmail);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Set user data for enhanced conversions
 * This improves conversion attribution accuracy
 */
export async function setUserData(user: AnalyticsUser): Promise<void> {
  if (!user.email) return;

  const hashedEmail = await hashEmail(user.email);
  const gtag = ensureGtag();

  gtag('set', 'user_data', {
    sha256_email_address: hashedEmail,
  });
}

/**
 * Track a conversion event
 */
export function trackConversion(
  config: ConversionConfig,
  value?: number,
  currency?: string,
  transactionId?: string
): void {
  const gtag = ensureGtag();
  const conversionParams: Record<string, unknown> = {
    send_to: `${config.id}/${config.label}`,
  };

  if (value !== undefined) {
    conversionParams.value = value;
    conversionParams.currency = currency || 'USD';
  }

  if (transactionId) {
    conversionParams.transaction_id = transactionId;
  }

  gtag('event', 'conversion', conversionParams);
}

/**
 * Track sign-up conversion
 */
export function trackSignUpConversion(config: ConversionConfig): void {
  trackConversion(config);
}

/**
 * Track purchase conversion with value
 */
export function trackPurchaseConversion(
  config: ConversionConfig,
  value: number,
  currency: string,
  transactionId?: string
): void {
  trackConversion(config, value, currency, transactionId);
}

/**
 * Track trial started conversion
 */
export function trackTrialConversion(config: ConversionConfig): void {
  trackConversion(config);
}
